'use no memo';

import type { Row } from '@tanstack/react-table';

import { CheckIcon, CircleSlashIcon, PencilIcon, XIcon } from 'lucide-react';

import { useTanstackTableDynamicContext } from '@/components/features/manage/tanstack-table-dynamic/TanstackTableDynamicProvider';
import { ButtonGroup } from '@/components/ui/button';
import { Button } from '@/components/ui/button';

/**
 * Props for TanstackTableDynamicEditCell.
 * @property {Row<TData>} row - The row object from Tanstack Table.
 */
interface DataTableEditCell<TData> {
  row: Row<TData>;
}

export function TanstackTableDynamicEditCell<TData>(dataTableEditCell: DataTableEditCell<TData>) {
  const { row } = dataTableEditCell;
  const { id, index } = row;
  const { saveChanges, setEditedRows, removeRow, editedRows, revertData, restoreOriginalData } = useTanstackTableDynamicContext();

  const handleSetEditedRows = () => {
    restoreOriginalData();
    setEditedRows((curr) => {
      return {
        ...curr,
        [id]: !curr[id],
      };
    });
  };

  const handleRevertData = () => {
    handleSetEditedRows();
  };

  const handleCancelRow = () => {
    handleSetEditedRows();
    revertData(index);
  };

  const handleSaveChanges = () => {
    handleSetEditedRows();
    saveChanges(index);
  };

  const handleRemoveRow = () => {
    removeRow(index);
  };

  return (
    <div className={'flex h-full justify-end gap-1'}>
      {editedRows[id] ? (
        <ButtonGroup>
          <Button variant="outline" size="sm" onClick={handleCancelRow} type={'button'} name={'cancel'}>
            <CircleSlashIcon />
          </Button>
          <Button variant="outline" size="sm" onClick={handleSaveChanges} type={'button'} name={'done'}>
            <CheckIcon className={'text-success!'} />
          </Button>
        </ButtonGroup>
      ) : (
        <ButtonGroup>
          <Button variant="outline" size="sm" onClick={handleRevertData} type={'button'} name={'edit'}>
            <PencilIcon />
          </Button>
          <Button variant="outline" size="sm" onClick={handleRemoveRow} type={'button'} name={'remove'}>
            <XIcon className={'text-destructive!'} />
          </Button>
        </ButtonGroup>
      )}
    </div>
  );
}
